import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { SpreadSheetRow, UploadContainer } from '@retail/products/components';
import { ContainedButton, Dialog, OutlinedButton, Stepper, TextButton } from '@shared/components';
import {
  customerMarkupImportConfig,
  recognizedCustomerFactorHeadings,
  recognizedGroupNumberHeadings,
  useImportCustomerMarkupConditionStore,
} from '@retail/customer-markup/store';
import { UploadCustomerMarkupValidation } from '@retail/customer-markup/components';
import { useCallback, useState } from 'react';
import { PriceListAssortmentSelect } from './Create/PriceListAssortmentSelect';
import { useCustomerMarkupCreate } from '@retail/customer-markup/context';
import { getImportedCustomerMarkupConditions } from '@retail/calculus/utils';
import { UploadedCustomerMarkup } from '@retail/customer-markup/types';
import { downloadTemplateCsv } from '@retail/products/utils';
import {
  recognizedGtinHeadings,
  recognizedMgItemNrHeadings,
  recognizedValidFromHeadings,
} from '@retail/utils';

interface Props {
  open: boolean;
  priceListAssortmentId?: number;
  supplierNumber: number;
  onClose: () => void;
}

const headings = [
  recognizedGroupNumberHeadings[0],
  recognizedGtinHeadings[0],
  recognizedMgItemNrHeadings[0],
  recognizedGtinHeadings[0],
  recognizedValidFromHeadings[0],
  recognizedCustomerFactorHeadings[0],
];

export const uploadCustomerMarkupCsvData = `\uFEFF${headings.join(';')}`;

export function UploadCustomerMarkupDialog({
  open,
  priceListAssortmentId,
  supplierNumber,
  onClose,
}: Props) {
  const t = useCalculusTFunction();

  const includeStepper = !priceListAssortmentId;
  const [selectedAssortmentIds, selectAssortmentId] = useState<number[]>([]);

  const { createCustomerMarkupConditions } = useCustomerMarkupCreate({
    assortmentIds: priceListAssortmentId ? [priceListAssortmentId] : selectedAssortmentIds,
  });

  const {
    importedFile,
    importSpreadsheet,
    clearState: clearImportState,
    run,
  } = useImportCustomerMarkupConditionStore();

  const validDataRows = run?.rows.filter((row) => row.status === 'ok');

  const handleImportSpreadsheet = useCallback(
    (rows: SpreadSheetRow[], file: File) =>
      importSpreadsheet(rows, file, customerMarkupImportConfig),
    [importSpreadsheet]
  );

  const handleCreateCustomerMarkupConditions = useCallback(() => {
    const conditions = getImportedCustomerMarkupConditions({
      customerMarkups: validDataRows?.map((row) => row.values) as UploadedCustomerMarkup[],
      supplierNumber,
    });
    createCustomerMarkupConditions(conditions);
  }, [createCustomerMarkupConditions, supplierNumber, validDataRows]);

  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    { stepperLabel: t('calculus.customerMarkup.upload'), stepTitle: 'ADD' },
    { stepperLabel: t('calculus.customerMarkup.selectCustomers'), stepTitle: 'EDIT' },
  ];
  const handleClose = () => {
    onClose();
    clearImportState();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {t('calculus.customerMarkup.upload')}
        {includeStepper && (
          <Stepper
            steps={steps.map((step) => step.stepperLabel)}
            activeStep={activeStep}
            labelOrientation="horizontal"
          />
        )}
      </DialogTitle>

      <DialogContent>
        {activeStep === 0 && (
          <UploadContainer
            isLarge
            importedFile={importedFile}
            onDownloadTemplate={() =>
              downloadTemplateCsv(
                uploadCustomerMarkupCsvData,
                'customerMarkupConditionsTemplate.csv'
              )
            }
            importSpreadsheet={handleImportSpreadsheet}
            resetImportedStates={clearImportState}
            renderValidation={
              run && (
                <UploadCustomerMarkupValidation
                  missingColumns={run.missingRequiredColumns}
                  invalidDataRows={run.rows
                    .filter((row) => row.status === 'error')
                    .map((row) => {
                      return {
                        values: row.values,
                        rowIndex: row.rowIndex,
                        rowErrors: row.errors,
                      };
                    })}
                  validDataRowsLength={(validDataRows ?? []).length}
                />
              )
            }
          />
        )}
        {activeStep === 1 && (
          <PriceListAssortmentSelect
            selectedAssortmentIds={selectedAssortmentIds}
            selectAssortmentId={selectAssortmentId}
          />
        )}
      </DialogContent>

      <DialogActions>
        <TextButton onClick={handleClose} size="small">
          {t('calculus.cancel')}
        </TextButton>
        {activeStep === 0 && includeStepper && (
          <ContainedButton
            size="small"
            onClick={() => setActiveStep(1)}
            disabled={!validDataRows || validDataRows.length < 1}
          >
            {t('calculus.customerMarkup.continueWithValidatedRow')}
          </ContainedButton>
        )}

        {(activeStep === 1 || !includeStepper) && (
          <>
            {includeStepper && (
              <OutlinedButton size="small" onClick={() => setActiveStep(0)}>
                {t('calculus.goBack')}
              </OutlinedButton>
            )}
            <ContainedButton
              size="small"
              onClick={() => {
                handleCreateCustomerMarkupConditions();
                handleClose();
              }}
              disabled={!priceListAssortmentId && selectedAssortmentIds.length === 0}
            >
              {t('calculus.toApproval')}
            </ContainedButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
